/**
 * useBiggestFunnelDrop
 *
 * Finds the biggest step-to-step drop across all saved funnels for the
 * account. Uses `getPageStats` (per-page pageviews for the period) to
 * approximate funnel-step traffic — accurate enough for "where's the
 * biggest leak" without needing a dedicated funnel analytics endpoint.
 *
 * Drives:
 *   - FUNNEL_DROP hero variant
 *
 * Approximation note: page-view counts don't perfectly equal unique users in
 * a funnel (a user may revisit a step). The signal is directionally right —
 * the biggest pageview gap between consecutive funnel steps is almost always
 * the biggest funnel drop. When the backend adds a per-funnel analytics
 * endpoint, swap the computation but keep the hook shape.
 */

import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getSavedFunnels, getPageStats } from '@/lib/api/api'

interface FunnelStepLike {
  stepId?: string
  eventType?: string
  eventTarget?: string
  order?: number
}

interface SavedFunnelLike {
  funnelId: string
  propertyId?: string
  name: string
  slug?: string
  steps: FunnelStepLike[]
}

interface UseBiggestFunnelDropInput {
  properties: Array<{
    propertyId: string
    name: string
    domain: string
    isDemo?: boolean
    archived?: boolean
  }>
}

interface BiggestFunnelDrop {
  funnelId: string
  funnelName: string
  propertyId: string
  propertyName: string
  /** Step URL we're dropping FROM. */
  from: string
  /** Step URL we're dropping TO. */
  to: string
  /** Pageviews on the FROM step. */
  viewsFrom: number
  /** Pageviews on the TO step. */
  viewsTo: number
  /** Drop percent, rounded — e.g. 68 for a 68% drop. */
  dropPct: number
}

interface UseBiggestFunnelDropResult {
  topDrop: BiggestFunnelDrop | null
  isLoading: boolean
}

/** Normalize a URL/path for matching — strip protocol, host, trailing slash. */
function normalizePath(url: string): string {
  if (!url) return ''
  let p = url.trim()
  // Strip protocol + host
  p = p.replace(/^https?:\/\/[^/]+/i, '')
  // Strip query/hash
  p = p.split('?')[0].split('#')[0]
  // Strip trailing slash (except root)
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  return p.toLowerCase()
}

/** Build a path → views lookup from the page stats response. */
function buildPageLookup(stats: any): Map<string, number> {
  const lookup = new Map<string, number>()
  // The response can come back as { pages: [...] } or just an array, and the
  // row shape varies — be defensive.
  const rows: any[] = Array.isArray(stats)
    ? stats
    : Array.isArray(stats?.pages)
      ? stats.pages
      : Array.isArray(stats?.data)
        ? stats.data
        : []
  for (const row of rows) {
    const path =
      row?.page ?? row?.path ?? row?.url ?? row?.pageUrl ?? row?.location ?? ''
    const views = Number(
      row?.page_views ?? row?.pageviews ?? row?.views ?? row?.count ?? 0
    )
    if (!path || !views) continue
    const norm = normalizePath(String(path))
    // Sum if the same normalized path appears multiple times
    lookup.set(norm, (lookup.get(norm) ?? 0) + views)
  }
  return lookup
}

/** Compute the biggest consecutive-step drop in a single funnel. */
function biggestDropInFunnel(
  funnel: SavedFunnelLike,
  pageLookup: Map<string, number>
): { from: string; to: string; viewsFrom: number; viewsTo: number; dropPct: number } | null {
  // Sort steps by `order`. page_view-typed steps are the ones we can match
  // against pageview data; mixed-event funnels need a different signal source
  // (we skip non-page steps quietly).
  const steps = [...funnel.steps]
    .filter((s) => s.eventType === 'page_view' && s.eventTarget)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  if (steps.length < 2) return null

  let biggest: ReturnType<typeof biggestDropInFunnel> = null

  for (let i = 0; i < steps.length - 1; i++) {
    const from = normalizePath(steps[i].eventTarget!)
    const to = normalizePath(steps[i + 1].eventTarget!)
    const viewsFrom = pageLookup.get(from) ?? 0
    const viewsTo = pageLookup.get(to) ?? 0
    // Skip steps we don't have data for — better to underreport than fabricate.
    if (viewsFrom === 0) continue
    const dropPct = Math.round(((viewsFrom - viewsTo) / viewsFrom) * 100)
    // Only consider drops — gains aren't a "leak."
    if (dropPct <= 0) continue
    if (!biggest || dropPct > biggest.dropPct) {
      biggest = { from, to, viewsFrom, viewsTo, dropPct }
    }
  }

  return biggest
}

export function useBiggestFunnelDrop({
  properties,
}: UseBiggestFunnelDropInput): UseBiggestFunnelDropResult {
  const { token } = useAuth()

  const targets = useMemo(
    () =>
      properties
        .filter((p) => !p.isDemo && !p.archived)
        .map((p) => ({ propertyId: p.propertyId, name: p.name, domain: p.domain })),
    [properties]
  )

  // ── Saved funnels per property ───────────────────────────────────────────
  const funnelsResults = useQueries({
    queries: targets.map(({ propertyId }) => ({
      // Match the queryKey used by useSavedFunnels so React Query dedupes the fetch.
      queryKey: ['savedFunnels', propertyId],
      queryFn: async () => {
        if (!token) return null
        try {
          const result = await getSavedFunnels(propertyId, token)
          return Array.isArray(result) ? result : []
        } catch {
          return null
        }
      },
      enabled: !!token,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  })

  // ── Page stats per property (only fetch where the property has saved funnels) ─
  const hasFunnelsByPropertyId = useMemo(() => {
    const m: Record<string, boolean> = {}
    targets.forEach((t, i) => {
      const arr = funnelsResults[i]?.data
      m[t.propertyId] = Array.isArray(arr) && arr.length > 0
    })
    return m
  }, [targets, funnelsResults])

  const pageStatsResults = useQueries({
    queries: targets.map(({ propertyId }) => ({
      queryKey: ['page-stats-7d', propertyId],
      queryFn: async () => {
        if (!token) return null
        try {
          return await getPageStats('LAST_7_DAYS', propertyId, token)
        } catch {
          return null
        }
      },
      // Skip page-stats fetch when the property has no saved funnels —
      // wasted work otherwise.
      enabled: !!token && !!hasFunnelsByPropertyId[propertyId],
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  })

  return useMemo<UseBiggestFunnelDropResult>(() => {
    let anyLoading = false
    let topDrop: BiggestFunnelDrop | null = null

    targets.forEach((t, i) => {
      const fnQ = funnelsResults[i]
      const psQ = pageStatsResults[i]
      if (fnQ?.isLoading) anyLoading = true
      if (psQ?.isLoading) anyLoading = true

      const funnels = fnQ?.data
      if (!Array.isArray(funnels) || funnels.length === 0) return
      const pageStats = psQ?.data
      if (!pageStats) return

      const pageLookup = buildPageLookup(pageStats)
      if (pageLookup.size === 0) return

      for (const funnel of funnels as SavedFunnelLike[]) {
        const drop = biggestDropInFunnel(funnel, pageLookup)
        if (!drop) continue
        // Bias slightly against tiny-traffic funnels — a 90% drop on 10 views
        // isn't actionable.
        if (drop.viewsFrom < 20) continue
        if (!topDrop || drop.dropPct > topDrop.dropPct) {
          topDrop = {
            funnelId: funnel.funnelId,
            funnelName: funnel.name,
            propertyId: t.propertyId,
            propertyName: t.name,
            from: drop.from,
            to: drop.to,
            viewsFrom: drop.viewsFrom,
            viewsTo: drop.viewsTo,
            dropPct: drop.dropPct,
          }
        }
      }
    })

    return { topDrop, isLoading: anyLoading }
  }, [targets, funnelsResults, pageStatsResults])
}
